/**
 * ScScopeSummaryCard (026, US3) — full-scope review card shown at the lock gate.
 * IN_CARD_ACTION widget: Lock + Edit buttons render inside the card via HeroShell actions.
 *
 * Uses HeroShell (variant='default') for the purple gradient icon tile, matching
 * the home-lifecycle HeroCard design pattern for approval-gate cards.
 */

import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';

import { HeroActionButtons, HeroShell, WidgetActionProps } from '../../widgets/chat-widget-ui';
import { capitalizeFirst } from '../../widgets/textFormat';
import { SkBar, SkBtn } from '../../widgets/skeletons';
import { ScStateBadge } from './ScStateBadge';
import type { ScScopeSummaryCardProps } from '@shared/widget-schemas';

type ScScopeSummaryCardComponentProps = ScScopeSummaryCardProps & WidgetActionProps;

export function ScScopeSummaryCard({
  projectName,
  decisionType,
  decision,
  // Default to [] so a producer that omits the field (undefined, before the Zod
  // default applies) never crashes — and the section simply stays hidden.
  bets = [],
  goals = [],
  draft,
  actions,
  onAction,
}: ScScopeSummaryCardComponentProps) {
  return (
    <HeroShell
      variant="default"
      icon={<Lock className="size-[22px]" strokeWidth={1.8} />}
      kicker="Scope summary"
      title={projectName}
      // The decision is NOT the header description — it reads as the thing being
      // tested, so it sits in its own labelled block directly above the bets it
      // governs (below), where "the decision → what we'll test → what we'll learn"
      // reads as one chain. The header keeps the project name alone.
      // Pills (project type + state) moved OUT of the top-right `badge` slot to a
      // bottom row (below) so the project title gets the full card width.
      // 039 phase 2 (1e) — once the scope is locked the card is CONFIRMED
      // (draft === false); render it read-only (no Lock/Edit) so a re-shown card
      // on resume can't offer a lock that's already a no-op. A draft or
      // unspecified card keeps its approval-gate actions.
      actions={
        draft !== false && actions && actions.length > 0 ? (
          <HeroActionButtons actions={actions} onAction={onAction} />
        ) : undefined
      }
    >
      {/* The decision — what the whole scope exists to answer. Sits directly above
          the bets so the card reads as one chain: the call, then what we'll test
          to make it, then what we'll learn. */}
      {decision && (
        <div data-testid="scope-summary-decision" className="flex flex-col gap-1.5">
          <SectionLabel>{decisionLabel(decisionType)}</SectionLabel>
          <p className="text-foreground text-[13px] leading-snug">{capitalizeFirst(decision)}</p>
        </div>
      )}

      {/* Bets — the substance rollup. The per-bet assumption COUNT was removed
          (2026-07-25): a bare number is not a quality signal — "3 assumptions"
          reads as progress while saying nothing about whether the right ones were
          surfaced — and it competed with the bet text for attention at exactly the
          moment the founder should be reading the bets themselves. The
          bet↔assumption structure is still enforced server-side by the lock gate;
          it just isn't a badge here. */}
      {bets.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <SectionLabel>What we'll test</SectionLabel>
          <ol className="flex flex-col divide-y divide-[var(--border)]">
            {bets.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 py-2.5 first:pt-0 last:pb-0">
                <NumberBadge n={i + 1} />
                <span className="text-foreground flex-1 text-[13px] leading-snug">{capitalizeFirst(b.text)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Learning goals */}
      {goals.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <SectionLabel>What we'll learn</SectionLabel>
          <ul className="flex flex-col gap-1.5">
            {goals.map((g, i) => (
              <li key={i} className="text-foreground flex items-start gap-2 text-[13px] leading-snug">
                <span aria-hidden className="mt-[7px] size-1.5 shrink-0 rounded-full bg-[#C084FC]" />
                <span className="flex-1">{capitalizeFirst(g)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bottom pill row — draft/confirmed state only. The project-type pill was
          removed (2026-07-25): the posture is now stated in words by the decision
          section's label ("THIS IS A VALIDATION PROJECT TO DECIDE:"), so a pill
          repeating it was saying the same thing twice, in the weaker place. */}
      <div data-testid="scope-summary-pills" className="flex flex-wrap items-center gap-2">
        <ScStateBadge draft={draft} />
      </div>
    </HeroShell>
  );
}

/**
 * The decision section's label — it states the project's posture in words rather
 * than leaving it to a pill: "THIS IS A VALIDATION PROJECT TO DECIDE:". Falls back
 * to a plain label when the posture isn't known yet, so an early/partial card never
 * renders a gap ("THIS IS A  PROJECT TO DECIDE:"). The article follows the word, so
 * Exploratory reads "AN".
 */
function decisionLabel(type?: 'Validation' | 'Exploratory'): string {
  if (!type) return 'The decision';
  const article = /^[AEIOU]/i.test(type) ? 'an' : 'a';
  return `This is ${article} ${type} project to decide:`;
}

/** Small uppercase section label ("Bets", "Learning goals"). */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-muted-foreground font-mono text-[10px] font-bold uppercase tracking-wider">{children}</span>
  );
}

/** Numbered index badge, matching the artifact cards. */
function NumberBadge({ n }: { n: number }) {
  return (
    <span className="mt-px inline-flex size-[18px] shrink-0 items-center justify-center rounded-full border border-[#7C3AED]/25 bg-[#7C3AED]/10 font-mono text-[9.5px] font-bold text-[#C084FC]">
      {n}
    </span>
  );
}

export function ScScopeSummaryCardSkeleton() {
  return (
    <div className="glass-card flex w-full max-w-[min(560px,100%)] flex-col gap-[18px] px-[26px] py-6">
      <div className="flex items-start gap-4">
        <div className="size-12 shrink-0 rounded-[14px]" style={{ background: 'var(--secondary)' }} />
        <div className="flex flex-1 flex-col gap-2">
          <SkBar width="30%" height="10px" />
          <SkBar width="60%" height="19px" />
          <SkBar width="75%" height="13px" />
        </div>
      </div>
      <div className="flex gap-3">
        <SkBar width="64px" height="52px" radius="12px" />
        <SkBar width="96px" height="52px" radius="12px" />
        <SkBar width="64px" height="52px" radius="12px" />
      </div>
      <SkBar width="85%" height="13px" />
      <div className="flex justify-end gap-2">
        <SkBtn width="80px" />
        <SkBtn width="110px" />
      </div>
    </div>
  );
}
